import { useId } from 'react'
import { ProCard } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Alert, App, Button, Col, Form, Input, Row, Space, Spin } from 'antd'
import { ArrowLeft } from 'lucide-react'
import type { CustomerUpsertInput } from '@super-mega/server/contracts/customer'
import { FormAnchorNav } from '@/components/form/form-anchor-nav.tsx'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { FORM_ERROR_SCROLL_OPTIONS } from '@/const/form.ts'
import { AttachmentUpload, type AttachmentDescriptor } from '@/features/files/index.ts'
import { CUSTOMER_FORM_SECTIONS } from '@/features/master-data/const/customer-form.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { CustomerBasicFields } from './customer-basic-fields.tsx'
import { type CustomerListSearch } from './customer-list-search.ts'
import { CustomerMonthlyBillingFields } from './customer-monthly-billing-fields.tsx'

export type CustomerEditorRecord = {
  id: number
  autoCollectionBillGrouping: boolean
  autoSendCollectionBillGroup: boolean
  collectionBillGroupingFrom?: string | null
  collectionBillGroupingDay?: number | null
  name: string
  type: 'normal' | 'agent'
  wecomChatId: string | null
  phone: string | null
  email: string | null
  note: string | null
  companyInfo: string | null
  tags: Array<{ id: number; name: string; color: string }>
  salesperson: { id: number; name: string; type: 'employee' | 'agent' } | null
  attachments: Array<{
    id: string | number
    file: {
      id: number
      fileName: string
      originName: string
      fileType: string | null
      fileSize: string | number
    }
  }>
}

export type CustomerFormValues = CustomerUpsertInput

export function CustomerForm({ customerId, returnSearch }: { customerId?: number; returnSearch: CustomerListSearch }) {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const validId = customerId !== undefined && Number.isSafeInteger(customerId) && customerId > 0
  // 复用服务端 ID 精确查询，编辑页刷新时也能独立读取完整档案。
  const detail = useQuery({
    ...trpc.customer.list.queryOptions({ current: 1, pageSize: 1, id: customerId }),
    enabled: validId,
    refetchOnWindowFocus: false
  })
  const salespeople = useQuery(trpc.lookup.members.queryOptions({ purpose: 'customer.read' }))
  const goBack = () => navigate({ to: '/customer-management/customers', search: returnSearch })
  const customer = detail.data?.data[0]
  if (customerId !== undefined && detail.isLoading) {
    return (
      <FormPageContainer title="编辑客户" breadcrumbRender={false}>
        <div className="flex min-h-72 items-center justify-center">
          <Spin description="正在读取客户档案…" />
        </div>
      </FormPageContainer>
    )
  }
  if (customerId !== undefined && (!validId || detail.isError || !customer)) {
    return (
      <FormPageContainer title="编辑客户" breadcrumbRender={false}>
        <Alert
          showIcon
          type="error"
          title={detail.isError ? getErrorMessage(detail.error, '客户档案加载失败') : '客户不存在或已删除'}
          action={
            <Space>
              <Button onClick={() => void goBack()}>返回列表</Button>
              {validId ? <Button onClick={() => void detail.refetch()}>重试</Button> : null}
            </Space>
          }
        />
      </FormPageContainer>
    )
  }
  const salespersonOptions = [...(salespeople.data ?? [])]
  if (customer?.salesperson && !salespersonOptions.some((person) => person.value === customer.salesperson?.id)) {
    salespersonOptions.push({
      label: customer.salesperson.name,
      value: customer.salesperson.id,
      type: customer.salesperson.type,
      isDisable: customer.salesperson.isDisable
    })
  }
  return (
    <FormPageContainer breadcrumbRender={false} title={customerId === undefined ? '新建客户' : '编辑客户'}>
      {salespeople.isError ? (
        <Alert
          className="mb-4"
          showIcon
          type="error"
          title="业务员选项加载失败"
          action={<Button onClick={() => void salespeople.refetch()}>重试</Button>}
        />
      ) : null}
      <CustomerEditorForm
        editing={customer ?? 'new'}
        onClose={() => void goBack()}
        onSaved={async () => {
          await goBack()
        }}
        salespersonOptions={salespersonOptions}
        salespeopleLoading={salespeople.isLoading}
      />
    </FormPageContainer>
  )
}

function getInitialAttachments(customer?: CustomerEditorRecord): AttachmentDescriptor[] {
  return (
    customer?.attachments.map(({ file }) => ({
      id: file.id,
      fileType: file.fileType,
      name: file.originName || file.fileName,
      size: Number(file.fileSize)
    })) ?? []
  )
}

function CustomerEditorForm({
  editing,
  onClose,
  onSaved,
  salespersonOptions,
  salespeopleLoading
}: {
  editing: CustomerEditorRecord | 'new'
  onClose: () => void
  onSaved: (customer: CustomerEditorRecord) => Promise<void>
  salespersonOptions: Array<{ label: string; value: number }>
  salespeopleLoading: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm<CustomerFormValues>()
  const formId = useId()
  const upsert = useMutation(trpc.customer.upsert.mutationOptions())
  const access = useAccess()
  const canEditBilling = access.can('customer.billing.update')
  const canEditChat = access.can('customer.chatBinding.update')

  const initialValues: Partial<CustomerFormValues> =
    editing === 'new'
      ? {
          autoCollectionBillGrouping: false,
          autoSendCollectionBillGroup: false,
          collectionBillGroupingDay: undefined,
          companyInfo: '',
          email: '',
          fileIds: [],
          name: '',
          note: '',
          phone: '',
          tagIds: [],
          type: 'normal'
        }
      : {
          autoCollectionBillGrouping: editing.autoCollectionBillGrouping,
          autoSendCollectionBillGroup: editing.autoSendCollectionBillGroup,
          collectionBillGroupingDay: editing.collectionBillGroupingDay ?? undefined,
          collectionBillGroupingFrom: editing.collectionBillGroupingFrom ?? null,
          companyInfo: editing.companyInfo ?? undefined,
          email: editing.email ?? undefined,
          fileIds: editing.attachments.map(({ file }) => file.id),
          name: editing.name,
          note: editing.note ?? undefined,
          phone: editing.phone ?? undefined,
          wecomChatId: editing.wecomChatId ?? undefined,
          salespersonId: editing.salesperson?.id,
          tagIds: editing.tags.map((tag) => tag.id),
          type: editing.type
        }

  const save = async (values: CustomerFormValues) => {
    try {
      const saved = await upsert.mutateAsync({
        id: editing === 'new' ? undefined : editing.id,
        data: {
          ...values,
          name: values.name.trim(),
          salespersonId: values.salespersonId ?? null,
          autoSendCollectionBillGroup: canEditBilling ? values.autoSendCollectionBillGroup : undefined,
          autoCollectionBillGrouping: canEditBilling ? values.autoCollectionBillGrouping : undefined,
          collectionBillGroupingDay: canEditBilling ? (values.collectionBillGroupingDay ?? null) : undefined,
          collectionBillGroupingFrom: canEditBilling ? values.collectionBillGroupingFrom?.trim() || null : undefined,
          companyInfo: values.companyInfo?.trim() || undefined,
          email: values.email?.trim() || undefined,
          note: values.note?.trim() || undefined,
          phone: values.phone?.trim() || undefined,
          wecomChatId: canEditChat ? values.wecomChatId?.trim() || null : undefined
        }
      })
      void message.success(editing === 'new' ? '客户档案已创建' : '客户档案已更新')
      await Promise.all([
        queryClient.invalidateQueries(trpc.customer.list.queryFilter()),
        queryClient.invalidateQueries(trpc.customer.billingCalendar.queryFilter())
      ])
      await onSaved(saved)
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '客户档案保存失败'))
      return false
    }
  }

  return (
    <div className="flex w-full flex-col gap-5 lg:flex-row lg:items-start">
      <div className="flex min-w-0 flex-1 flex-col">
        <Form<CustomerFormValues>
          id={formId}
          form={form}
          initialValues={initialValues}
          layout="vertical"
          disabled={upsert.isPending}
          onFinish={save}
          scrollToFirstError={FORM_ERROR_SCROLL_OPTIONS}
        >
          <div className="flex flex-col gap-4 pb-6">
            <section className="scroll-mt-24" id="section-basic">
              <ProCard headerBordered title="基本信息" variant="outlined">
                <CustomerBasicFields
                  salespersonOptions={salespersonOptions}
                  salespeopleLoading={salespeopleLoading}
                  currentTagOptions={editing === 'new' ? [] : editing.tags}
                />
              </ProCard>
            </section>
            <section className="scroll-mt-24" id="section-contact">
              <ProCard headerBordered title="联系方式" variant="outlined">
                <Row gutter={16}>
                  <Col sm={12} xs={24}>
                    <Form.Item label="客户手机" name="phone" rules={[{ max: 20 }]}>
                      <Input placeholder="请输入联系电话" />
                    </Form.Item>
                  </Col>
                  <Col sm={12} xs={24}>
                    <Form.Item label="客户邮箱" name="email" rules={[{ max: 255, type: 'email' }]}>
                      <Input placeholder="请输入联系邮箱" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label="企微群 ID"
                      name="wecomChatId"
                      extra="每个客户关联一个企微群，留空表示未关联。"
                      rules={[{ max: 255, message: '企微群 ID 最多 255 个字符' }]}
                    >
                      <Input
                        allowClear
                        disabled={!canEditChat || upsert.isPending}
                        placeholder="请输入此客户对应的企微群 ID"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </ProCard>
            </section>
            <section className="scroll-mt-24" id="section-billing">
              <ProCard headerBordered title="账单设置" variant="outlined">
                <CustomerMonthlyBillingFields disabled={!canEditBilling || upsert.isPending} />
              </ProCard>
            </section>
            <section className="scroll-mt-24" id="section-attachments">
              <ProCard headerBordered title="附件与备注" variant="outlined">
                <Form.Item label="附件" name="fileIds">
                  <AttachmentUpload
                    purpose="customerAttachment"
                    initialFiles={getInitialAttachments(editing === 'new' ? undefined : editing)}
                  />
                </Form.Item>
                <Form.Item label="备注" name="note">
                  <Input.TextArea maxLength={5000} placeholder="补充客户背景、合作偏好等信息" rows={3} showCount />
                </Form.Item>
              </ProCard>
            </section>
          </div>
        </Form>
        <div className="border-border bg-surface/95 sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 shadow-xs backdrop-blur">
          <Button icon={<ArrowLeft aria-hidden size={16} />} disabled={upsert.isPending} onClick={onClose}>
            返回列表
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={upsert.isPending}
              onClick={() => {
                form.resetFields()
              }}
            >
              {editing === 'new' ? '重置' : '恢复已保存内容'}
            </Button>
            <Button form={formId} htmlType="submit" loading={upsert.isPending} type="primary">
              保存客户
            </Button>
          </div>
        </div>
      </div>
      <FormAnchorNav className="hidden lg:block" sections={CUSTOMER_FORM_SECTIONS} />
    </div>
  )
}
